import React, { useContext, useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Link,
} from "react-router-dom";
import Navbar from "./components/Navbar";
import Home from "./components/Home";
import CSVUpload from "./components/CSVUpload";
import DataList from "./components/DataList";
import PendingQuestions from "./components/PendingQuestions";
import WorksheetGenerator from "./components/WorksheetGenerator";
import MyWorksheets from "./components/MyWorksheets";
import SharedWorksheets from "./components/SharedWorksheets";
import UserManagement from "./components/UserManagement";
import AdminDataView from "./components/AdminDataView";
import Login from "./components/Login";
import Register from "./components/Register";
import LoginCheck from "./components/LoginCheck";
import PrivateRoute from "./components/PrivateRoute";
import { AuthProvider } from "./contexts/AuthContext";
import AuthContext from "./contexts/AuthContext";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import GoogleCallback from "./components/GoogleCallback";
import CorsTest from "./components/CorsTest";
import { Container } from "react-bootstrap";
import api, { publicApi } from "./api";
import MyQuestions from "./components/MyQuestions";
import SharedQuestions from "./components/SharedQuestions";
import QuestionImageUpload from "./components/QuestionImageUpload";
import FlaggedQuestions from "./components/FlaggedQuestions";
import RabbitLogin from "./components/RabbitLogin";
import PaymentMethodManager from "./components/PaymentMethodManager";
import SubscriptionMetricsDashboard from "./components/subscription/SubscriptionMetricsDashboard";
// Commenting out problematic import
// import SubscriptionAuditLogs from "./components/subscription/SubscriptionAuditLogs";

// ViewOnlyRoute redirects to DataList if user has read-only permissions
const ViewOnlyRoute = ({ children }) => {
  const { auth } = useContext(AuthContext);

  // First check if user is admin - admins should have access to everything
  const isAdmin =
    auth.isAdmin ||
    (auth.user && (auth.user.is_staff || auth.user.is_superuser));

  if (isAdmin) {
    return children;
  }

  // For non-admin users, check if they're view-only
  const isViewOnly =
    auth.isAuthenticated &&
    auth.user &&
    !auth.user.is_staff &&
    !auth.user.is_superuser &&
    auth.user.profile &&
    !auth.user.profile.can_create &&
    !auth.user.profile.can_edit &&
    !auth.user.profile.can_delete;

  return isViewOnly ? <Navigate to="/data" /> : children;
};

// AdminRoute ensures only admin users can access certain pages
const AdminRoute = ({ children }) => {
  const { auth } = useContext(AuthContext);

  const isAdmin =
    auth.isAdmin ||
    (auth.isAuthenticated &&
      auth.user &&
      (auth.user.is_staff || auth.user.is_superuser));

  return isAdmin ? children : <Navigate to="/data" />;
};

// Add a simple NotFound component
const NotFound = () => (
  <div className="text-center mt-5">
    <h2>404 - Page Not Found</h2>
    <p>The page you are looking for does not exist or has been moved.</p>
    <p>
      <Link to="/">Go to Home Page</Link>
    </p>
  </div>
);

// This special hook prevents the AuthContext from redirecting
// Specifically used for the rabbit login route
const useRabbitAuthCheck = () => {
  const { auth } = useContext(AuthContext);
  // If a user is already authenticated and tries to access /rabbit,
  // we'll let them stay on the page instead of redirecting
  return {
    auth,
    shouldRedirect: false,
  };
};

const AppRoutes = () => {
  const [systemSettings, setSystemSettings] = useState({
    enable_registration: "true",
    enable_google_login: "true",
  });

  useEffect(() => {
    const fetchSystemSettings = async () => {
      try {
        console.log("App.js: Fetching system settings from API...");
        const response = await publicApi.get("/api/public-settings/");
        console.log("App.js: System settings response:", response.data);
        if (response && response.data) {
          setSystemSettings(response.data);
        } else {
          // If we get an empty response, keep using defaults
          console.log("App.js: Empty response, using default system settings");
        }
      } catch (err) {
        console.error("Error fetching system settings:", err);
        // Continue using default values from initial state
        console.log("App.js: Using default system settings due to API error");
      }
    };

    // Set up a timeout to ensure we don't block rendering if the API is slow
    const timeoutId = setTimeout(() => {
      console.log("App.js: System settings fetch timeout, using defaults");
    }, 2000);

    fetchSystemSettings().finally(() => {
      clearTimeout(timeoutId);
    });
  }, []);

  return (
    <Routes>
      {/* Redirect from root to /data */}
      <Route path="/" element={<Navigate to="/data" replace />} />

      {/* Public routes */}
      <Route path="/login" element={<Login />} />

      {/* Rabbit login route with no redirects */}
      <Route path="/rabbit" element={<RabbitLogin />} />

      {/* Registration route - conditionally rendered */}
      {systemSettings.enable_registration === "true" && (
        <Route path="/register" element={<Register />} />
      )}

      <Route path="/check-login" element={<LoginCheck />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />

      {/* Google OAuth callback route */}
      {systemSettings.enable_google_login === "true" && (
        <Route path="/auth/google/callback" element={<GoogleCallback />} />
      )}

      {/* CORS Test route - for development */}
      <Route path="/cors-test" element={<CorsTest />} />

      {/* Protected routes */}
      <Route
        path="/home"
        element={
          <PrivateRoute requireSubscription={true}>
            <ViewOnlyRoute>
              <Home />
            </ViewOnlyRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/upload"
        element={
          <PrivateRoute bypassSubscriptionCheck={true}>
            <ViewOnlyRoute>
              <CSVUpload />
            </ViewOnlyRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/data"
        element={
          <PrivateRoute requireSubscription={true}>
            <DataList />
          </PrivateRoute>
        }
      />
      <Route
        path="/worksheets"
        element={
          <PrivateRoute requireSubscription={true}>
            <WorksheetGenerator />
          </PrivateRoute>
        }
      />
      <Route
        path="/my-worksheets"
        element={
          <PrivateRoute requireSubscription={true}>
            <MyWorksheets />
          </PrivateRoute>
        }
      />
      <Route
        path="/shared-worksheets"
        element={
          <PrivateRoute requireSubscription={true}>
            <SharedWorksheets />
          </PrivateRoute>
        }
      />

      {/* Admin-only routes - bypass subscription checks */}
      <Route
        path="/admin-data"
        element={
          <PrivateRoute bypassSubscriptionCheck={true}>
            <AdminRoute>
              <AdminDataView />
            </AdminRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/pending-questions"
        element={
          <PrivateRoute bypassSubscriptionCheck={true}>
            <AdminRoute>
              <PendingQuestions />
            </AdminRoute>
          </PrivateRoute>
        }
      />

      {/* User management route - admin only */}
      <Route
        path="/user-management"
        element={
          <PrivateRoute bypassSubscriptionCheck={true}>
            <AdminRoute>
              <UserManagement />
            </AdminRoute>
          </PrivateRoute>
        }
      />

      {/* Premium features - require subscription */}
      <Route
        path="/my-questions"
        element={
          <PrivateRoute requireSubscription={true}>
            <MyQuestions />
          </PrivateRoute>
        }
      />
      <Route
        path="/shared-questions"
        element={
          <PrivateRoute requireSubscription={true}>
            <SharedQuestions />
          </PrivateRoute>
        }
      />
      <Route
        path="/upload-question"
        element={
          <PrivateRoute requireSubscription={true}>
            <QuestionImageUpload />
          </PrivateRoute>
        }
      />

      {/* Payment method management - Issue #11 */}
      <Route
        path="/payment-methods"
        element={
          <PrivateRoute requireSubscription={true}>
            <PaymentMethodManager />
          </PrivateRoute>
        }
      />

      {/* Test route for flagged questions - require subscription */}
      <Route
        path="/test-flagged-questions"
        element={
          <PrivateRoute requireSubscription={true}>
            <FlaggedQuestions />
          </PrivateRoute>
        }
      />

      {/* Subscription metrics route - admin only */}
      <Route
        path="/subscription-metrics"
        element={
          <PrivateRoute bypassSubscriptionCheck={true}>
            <AdminRoute>
              <SubscriptionMetricsDashboard />
            </AdminRoute>
          </PrivateRoute>
        }
      />

      {/* Subscription Audit Logs - Issue #14 */}
      {/* Commenting out audit logs route to fix build error
      <Route
        path="/subscription-audit-logs"
        element={
          <PrivateRoute bypassSubscriptionCheck={true}>
            <AdminRoute>
              <SubscriptionAuditLogs />
            </AdminRoute>
          </PrivateRoute>
        }
      />
      */}

      {/* Catch-all route for any unmatched routes */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <Container fluid className="p-0">
          <Navbar />
          <Container className="mt-3">
            <AppRoutes />
          </Container>
        </Container>
      </AuthProvider>
    </Router>
  );
}

export default App;
// Small change to force a new build
